/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Save, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { useAdmin } from './AdminAuth';
import { useContent, DEFAULT_CONTENT } from '../context/ContentContext';

const Field = ({ label, value, onChange, textarea }) => (
  <div className="mb-3">
    <label className="block text-xs font-semibold text-[#0f1712]/60 mb-1.5">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full rounded-xl border border-[#0f1712]/15 px-4 py-2.5 text-sm outline-none focus:border-[#0e9f6e] transition" />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-[#0f1712]/15 px-4 py-2.5 text-sm outline-none focus:border-[#0e9f6e] transition" />
    )}
  </div>
);

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6 mb-6">
    <h3 className="font-display text-lg font-700 text-[#0f1712] mb-4">{title}</h3>
    {children}
  </div>
);

const ContentPanel = () => {
  const { authAxios } = useAdmin();
  const { refresh } = useContent();
  const [data, setData] = useState(DEFAULT_CONTENT);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const res = await authAxios.get('/content');
    setData({ ...DEFAULT_CONTENT, ...res.data });
  };
  useEffect(() => { load(); }, []);

  const setHero = (k, v) => setData((d) => ({ ...d, hero: { ...d.hero, [k]: v } }));
  const setArrItem = (key, i, field, v) => setData((d) => {
    const arr = [...d[key]]; arr[i] = { ...arr[i], [field]: v }; return { ...d, [key]: arr };
  });
  const addItem = (key, tpl) => setData((d) => ({ ...d, [key]: [...d[key], tpl] }));
  const removeItem = (key, i) => setData((d) => ({ ...d, [key]: d[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await authAxios.put('/content', { data });
      await refresh();
      setMsg('Saved! Changes are live on the site.');
    } catch (e) { setMsg('Failed to save. Please try again.'); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 4000); }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset all content back to defaults?')) return;
    await authAxios.post('/content/reset');
    await load(); await refresh();
    setMsg('Content reset to defaults.');
    setTimeout(() => setMsg(''), 4000);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 sticky top-4 z-10">
        <div>
          <h2 className="font-display text-2xl font-700 text-[#0f1712]">Site content</h2>
          <p className="text-[#0f1712]/50 text-sm mt-1">Edit the live homepage & pricing text.</p>
        </div>
        <div className="flex items-center gap-2">
          {msg && <span className="text-sm text-[#0e9f6e] font-medium">{msg}</span>}
          <button onClick={resetAll} className="inline-flex items-center gap-2 rounded-xl border border-[#0f1712]/15 px-4 py-2.5 text-sm font-semibold text-[#0f1712] hover:border-[#0e9f6e] transition-colors">
            <RotateCcw size={15} /> Reset
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#0e9f6e] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0c8a60] transition-colors disabled:opacity-60">
            <Save size={15} /> {saving ? 'Saving\u2026' : 'Save changes'}
          </button>
        </div>
      </div>

      <Card title="Hero section">
        <Field label="Badge" value={data.hero.badge} onChange={(v) => setHero('badge', v)} />
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Title (start)" value={data.hero.title} onChange={(v) => setHero('title', v)} />
          <Field label="Title (accent)" value={data.hero.titleAccent} onChange={(v) => setHero('titleAccent', v)} />
          <Field label="Title (end)" value={data.hero.titleEnd} onChange={(v) => setHero('titleEnd', v)} />
        </div>
        <Field label="Subtitle" value={data.hero.subtitle} onChange={(v) => setHero('subtitle', v)} textarea />
      </Card>

      <Card title="Stats">
        <div className="grid sm:grid-cols-2 gap-3">
          {data.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 items-end">
              <Field label={`Value ${i + 1}`} value={s.value} onChange={(v) => setArrItem('stats', i, 'value', v)} />
              <Field label="Label" value={s.label} onChange={(v) => setArrItem('stats', i, 'label', v)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Pricing plans">
        {data.pricing.map((p, i) => (
          <div key={i} className="border border-[#0f1712]/10 rounded-xl p-4 mb-3">
            <div className="grid sm:grid-cols-3 gap-2">
              <Field label="Name" value={p.name} onChange={(v) => setArrItem('pricing', i, 'name', v)} />
              <Field label="Price" value={p.price} onChange={(v) => setArrItem('pricing', i, 'price', v)} />
              <Field label="Unit" value={p.unit} onChange={(v) => setArrItem('pricing', i, 'unit', v)} />
            </div>
            <Field label="Features (comma separated)" value={(p.features || []).join(', ')}
              onChange={(v) => setArrItem('pricing', i, 'features', v.split(',').map((x) => x.trim()).filter(Boolean))} />
          </div>
        ))}
      </Card>

      <Card title="Testimonials">
        {data.testimonials.map((t, i) => (
          <div key={i} className="border border-[#0f1712]/10 rounded-xl p-4 mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-[#0f1712]/50">#{i + 1}</span>
              <button onClick={() => removeItem('testimonials', i)} className="text-[#0f1712]/40 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
            <Field label="Quote" value={t.text} onChange={(v) => setArrItem('testimonials', i, 'text', v)} textarea />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name" value={t.name} onChange={(v) => setArrItem('testimonials', i, 'name', v)} />
              <Field label="Role" value={t.role} onChange={(v) => setArrItem('testimonials', i, 'role', v)} />
            </div>
          </div>
        ))}
        <button onClick={() => addItem('testimonials', { text: '', name: '', role: '' })} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0e9f6e]"><Plus size={15} /> Add testimonial</button>
      </Card>

      <Card title="FAQ">
        {data.faqs.map((f, i) => (
          <div key={i} className="border border-[#0f1712]/10 rounded-xl p-4 mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-[#0f1712]/50">#{i + 1}</span>
              <button onClick={() => removeItem('faqs', i)} className="text-[#0f1712]/40 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
            <Field label="Question" value={f.q} onChange={(v) => setArrItem('faqs', i, 'q', v)} />
            <Field label="Answer" value={f.a} onChange={(v) => setArrItem('faqs', i, 'a', v)} textarea />
          </div>
        ))}
        <button onClick={() => addItem('faqs', { q: '', a: '' })} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0e9f6e]"><Plus size={15} /> Add question</button>
      </Card>
    </div>
  );
};

export default ContentPanel;
